import { createApi } from '@reduxjs/toolkit/query/react';

import {
	Credits,
	GoogleSheetsUrl,
	TopIdsWithFiltersCandidates,
	TopIdsWithFiltersCompanies
} from 'shared/generated-models';
import { voidFn } from 'shared/utils';
import { ProjectCandidatesExportRequest } from 'features/csv-export';
import { environment } from 'environments/environment';

import { interceptor } from './interceptor';
import { authService } from './auth.service';

const GOOGLE_AUTH_REQUIRED_CODE = 'google_auth_required';
// Set before redirecting to Google consent so a failing replay can't redirect again
const GOOGLE_AUTH_REDIRECTED_KEY = 'googleSheetsAuthRedirected';

const isGoogleAuthRequired = (err: unknown): boolean =>
	(err as { error?: { data?: { code?: string } } })?.error?.data?.code === GOOGLE_AUTH_REQUIRED_CODE;

const handleGoogleSheetsError = (err: unknown, storageKey: string, params: unknown) => {
	console.error(err);
	if (!isGoogleAuthRequired(err) || sessionStorage.getItem(GOOGLE_AUTH_REDIRECTED_KEY)) {
		sessionStorage.removeItem(GOOGLE_AUTH_REDIRECTED_KEY);
		return;
	}
	try {
		localStorage.setItem(storageKey, JSON.stringify(params));
	} catch (storageErr) {
		console.error('Failed to save params to localStorage:', storageErr);
	}
	sessionStorage.setItem(GOOGLE_AUTH_REDIRECTED_KEY, '1');
	const currentUrl = encodeURIComponent(window.location.href);
	const errorUrl = `${window.location.origin}/login-error`;
	const authLink = `${environment.apiUrl}/login/google-oauth2?redirect_to=${currentUrl}&error_to=${errorUrl}&force_consent=1`;
	window.location.href = authLink;
};

export const csvService = createApi({
	reducerPath: 'csvService',
	baseQuery: interceptor,
	endpoints: builder => ({
		getCsv: builder.query<Credits, TopIdsWithFiltersCandidates>({
			query: params => ({
				url: '/download_csv/',
				method: 'POST',
				body: params
			}),
			async onQueryStarted(csv, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(
						authService.util.updateQueryData('getCurrentUser', voidFn(), user => {
							return { ...user, credits: data.credits_left };
						})
					);
				} catch (err) {
					console.error(err);
				}
			}
		}),
		getGoogleSheets: builder.query<GoogleSheetsUrl, TopIdsWithFiltersCandidates>({
			query: params => ({
				url: '/google_sheets/',
				method: 'POST',
				body: params
			}),
			async onQueryStarted(params, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					sessionStorage.removeItem(GOOGLE_AUTH_REDIRECTED_KEY);
					dispatch(
						authService.util.updateQueryData('getCurrentUser', voidFn(), user => {
							return { ...user, credits: data.credits_left };
						})
					);
					window.open(data.url, '_blank');
				} catch (err) {
					handleGoogleSheetsError(err, 'peopleParams', params);
				}
			}
		}),
		getProjectContactsCsv: builder.query<Credits, ProjectCandidatesExportRequest>({
			query: params => ({
				url: `/projects/${params.project_pk}/download_csv/`,
				method: 'POST',
				body: params.candidates
			}),
			async onQueryStarted(csv, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(
						authService.util.updateQueryData('getCurrentUser', voidFn(), user => {
							return { ...user, credits: data.credits_left };
						})
					);
				} catch (err) {
					console.error(err);
				}
			}
		}),
		getProjectContactsGoogleSheets: builder.query<GoogleSheetsUrl, ProjectCandidatesExportRequest>({
			query: params => ({
				url: `/projects/${params.project_pk}/google_sheets/`,
				method: 'POST',
				body: params.candidates
			}),
			async onQueryStarted(params, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					sessionStorage.removeItem(GOOGLE_AUTH_REDIRECTED_KEY);
					dispatch(
						authService.util.updateQueryData('getCurrentUser', voidFn(), user => {
							return { ...user, credits: data.credits_left };
						})
					);
				} catch (err) {
					handleGoogleSheetsError(err, 'projectParams', params);
				}
			}
		}),
		getOpenedContactsCsv: builder.query<Credits, TopIdsWithFiltersCandidates>({
			query: params => ({
				url: `/opened_contacts/download_csv/`,
				method: 'POST',
				body: params
			}),
			async onQueryStarted(csv, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(
						authService.util.updateQueryData('getCurrentUser', voidFn(), user => {
							return { ...user, credits: data.credits_left };
						})
					);
				} catch (err) {
					console.error(err);
				}
			}
		}),
		getOpenedContactsGoogleSheets: builder.query<GoogleSheetsUrl, TopIdsWithFiltersCandidates>({
			query: params => ({
				url: `/opened_contacts/google_sheets/`,
				method: 'POST',
				body: params
			}),
			async onQueryStarted(csv, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(
						authService.util.updateQueryData('getCurrentUser', voidFn(), user => {
							return { ...user, credits: data.credits_left };
						})
					);
				} catch (err) {
					console.error(err);
				}
			}
		}),
		getTechnographicsCsv: builder.query<Credits, TopIdsWithFiltersCompanies>({
			query: params => ({
				url: '/technographics/download_csv/',
				method: 'POST',
				body: params
			}),
			async onQueryStarted(csv, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(
						authService.util.updateQueryData('getCurrentUser', voidFn(), user => {
							return { ...user, credits: data.credits_left };
						})
					);
				} catch (err) {
					console.error(err);
				}
			}
		}),
		getTechnographicsGoogleSheets: builder.query<GoogleSheetsUrl, TopIdsWithFiltersCompanies>({
			query: params => ({
				url: '/technographics/google_sheets/',
				method: 'POST',
				body: params
			}),
			async onQueryStarted(params, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					sessionStorage.removeItem(GOOGLE_AUTH_REDIRECTED_KEY);
					dispatch(
						authService.util.updateQueryData('getCurrentUser', voidFn(), user => {
							return { ...user, credits: data.credits_left };
						})
					);
					window.open(data.url, '_blank');
				} catch (err) {
					handleGoogleSheetsError(err, 'technographicsCompanyParams', params);
				}
			}
		})
	})
});

export const {
	useLazyGetCsvQuery,
	useLazyGetGoogleSheetsQuery,
	useLazyGetProjectContactsCsvQuery,
	useLazyGetProjectContactsGoogleSheetsQuery,
	useLazyGetOpenedContactsCsvQuery,
	useLazyGetOpenedContactsGoogleSheetsQuery,
	useLazyGetTechnographicsCsvQuery,
	useLazyGetTechnographicsGoogleSheetsQuery
} = csvService;
